/**
 * Lobby store —— 大厅状态(玩家列表 + 房间列表 + 我的资料镜像)。
 *
 * 数据流:
 * - `lobby_state` 帧 → applyLobbyState(): 整体替换 (snapshot 即真相);
 * - `lobby_event` 帧 → applyLobbyEvent(): 增量 patch (M3 才接入);
 * - 组件只读 store, 不直接操作 Map / WebSocket。
 *
 * 选择 Map 而非数组:O(1) 按 pid/room_id 查询和 patch;
 * computed 派生有序数组给视图。
 */

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type {
  LobbyEventPayload,
  LobbyStateFrame,
  MePayload,
  PlayerSummary,
  RoomCard,
} from '@/ws/frames'

export const useLobbyStore = defineStore('lobby', () => {
  // 是否收到过首个 lobby_state(用于 UI 区分"加载中"和"列表为空")
  const ready = ref(false)

  // 索引存储
  const roomsById   = ref<Map<string, RoomCard>>(new Map())
  const playersById = ref<Map<string, PlayerSummary>>(new Map())
  /** 我的在线资料镜像; 与 auth.me 字段重叠但语义不同 ——
   *  auth.me 来自 REST, lobby.me 来自 WS 推送的实时值 (含 status / room_id 等)。*/
  const me = ref<MePayload | null>(null)

  // ── 派生 ────────────────────────────────────────────────────

  /** 房间按 player_count 降序 → big_blind 降序 → room_id 字典序。*/
  const rooms = computed<RoomCard[]>(() => {
    return [...roomsById.value.values()].sort((a, b) => {
      if (a.player_count !== b.player_count) return b.player_count - a.player_count
      if (a.big_blind    !== b.big_blind)    return b.big_blind    - a.big_blind
      return a.room_id.localeCompare(b.room_id)
    })
  })

  /** 玩家按 self → playing → online → offline → name。后端已排好,
   *  这里仅做 fallback (lobby_event 增量后顺序可能漂)。*/
  const players = computed<PlayerSummary[]>(() => {
    const order: Record<PlayerSummary['status'], number> = {
      playing: 0, online: 1, offline: 2,
    }
    return [...playersById.value.values()].sort((a, b) => {
      if (a.is_self  !== b.is_self)  return a.is_self ? -1 : 1
      const oa = order[a.status] ?? 3
      const ob = order[b.status] ?? 3
      if (oa !== ob) return oa - ob
      return a.name.localeCompare(b.name)
    })
  })

  // ── 写入 ────────────────────────────────────────────────────

  function applyLobbyState(frame: LobbyStateFrame): void {
    roomsById.value   = new Map(frame.rooms.map(r => [r.room_id, r]))
    playersById.value = new Map(frame.players.map(p => [p.pid, p]))
    me.value          = frame.me
    ready.value       = true
  }

  /** lobby_event 增量 patch。snapshot is truth, 这里只是减少 round-trip。
   *  若 patch 对象不在索引里 (e.g. player_offline 但本地没有该玩家),
   *  通常意味着 lobby_state 与 event 顺序错乱(极少见, 后续 lobby_state 会自愈),
   *  此处静默忽略而非报错。
   */
  function applyLobbyEvent(evt: LobbyEventPayload): void {
    switch (evt.kind) {
      case 'room_added':
      case 'room_updated':
        roomsById.value.set(evt.data.room_id, evt.data)
        roomsById.value = new Map(roomsById.value)  // 触发 reactive
        break

      case 'room_removed':
        if (roomsById.value.delete(evt.data.room_id)) {
          roomsById.value = new Map(roomsById.value)
        }
        break

      case 'player_online': {
        const prev = playersById.value.get(evt.data.pid)
        const next: PlayerSummary = {
          pid:     evt.data.pid,
          name:    evt.data.name,
          money:   evt.data.money,
          status:  evt.data.status,
          avatar:  evt.data.avatar,
          is_self: prev?.is_self ?? (evt.data.pid === me.value?.pid),
        }
        playersById.value.set(evt.data.pid, next)
        playersById.value = new Map(playersById.value)
        break
      }

      case 'player_offline': {
        const prev = playersById.value.get(evt.data.pid)
        if (!prev) break
        playersById.value.set(evt.data.pid, { ...prev, status: 'offline' })
        playersById.value = new Map(playersById.value)
        break
      }

      case 'player_status_changed': {
        const prev = playersById.value.get(evt.data.pid)
        const next: PlayerSummary = {
          pid:     evt.data.pid,
          name:    evt.data.name,
          money:   evt.data.money,
          status:  evt.data.status,
          avatar:  evt.data.avatar,
          is_self: prev?.is_self ?? (evt.data.pid === me.value?.pid),
        }
        playersById.value.set(evt.data.pid, next)
        playersById.value = new Map(playersById.value)
        // self 同步 me 上的 room_id / status / money —— "你在哪个房间" 是它的真相。
        if (next.is_self && me.value) {
          me.value = {
            ...me.value,
            name:    evt.data.name,
            money:   evt.data.money,
            status:  evt.data.status,
            room_id: evt.data.room_id,
            avatar:  evt.data.avatar,
          }
        }
        break
      }

      case 'profile_updated': {
        const prev = playersById.value.get(evt.data.pid)
        if (prev) {
          playersById.value.set(evt.data.pid, {
            ...prev, name: evt.data.name, avatar: evt.data.avatar,
          })
          playersById.value = new Map(playersById.value)
        }
        if (me.value?.pid === evt.data.pid) {
          me.value = { ...me.value, name: evt.data.name, avatar: evt.data.avatar }
        }
        break
      }

      case 'notification':
        // 通知由 useWebSocket 路由到 notifications store, 这里不再处理。
        break
    }
  }

  function reset(): void {
    roomsById.value.clear()
    playersById.value.clear()
    me.value = null
    ready.value = false
  }

  return {
    ready, me, rooms, players, roomsById, playersById,
    applyLobbyState, applyLobbyEvent, reset,
  }
})
